import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { clearActionMeta, dataMetaSelector } from "Bands/state";

import { AccommodationReservationForm } from "Bands/components/AccommodationReservationForm/AccommodationReservationForm";
import {
  createAccommodationReservation,
  dispatchGetEventAccommodationReservations,
} from "Bands/state/data/actions/eventBlocks/eventAccommodationReservation";

import {
  BAND_ACTIONS,
  IAccommodationBlock,
  IAccommodationReservationFormParams,
  IAccommodationReservationParams,
} from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { User } from "shared/assets/icons";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IFormButtons, IJSONAPIErrorCodes, IStatus } from "shared/types";
import { dispatchGetEventAccommodationRoomsForBlock } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoom";
import { dispatchGetEventAccommodationRoomTypes } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoomType";

const { accommodationBlock: blockCopy } = copy;

interface Props {
  handleClose: () => void;
  block: IAccommodationBlock;
}

export const CreateAccommodationReservationModal: React.FC<Props> = ({ handleClose, block }) => {
  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createEventAccommodationReservation)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.createEventAccommodationReservation);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      handleClose();

      dispatch(clearActionMeta(BAND_ACTIONS.createEventAccommodationReservation));

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: blockCopy.create.peopleBlock.messages.success,
        })
      );

      dispatchGetEventAccommodationReservations({
        dispatch,
        blockId: block.id,
      });

      dispatchGetEventAccommodationRoomTypes(dispatch, block.id);
      dispatchGetEventAccommodationRoomsForBlock(dispatch, block.id);
    }
  }, [apiMeta?.status, dispatch, handleClose, block.id]);

  const handleSubmit = ({ people, roomType, room }: IAccommodationReservationFormParams) => {
    const data: IAccommodationReservationParams = {
      accommodationBlock: API_ENDPOINTS.EVENT_ACCOMMODATION_BLOCK(block.id),
      people: people?.map(person => API_ENDPOINTS.BAND_USER(person.value)),

      ...(roomType &&
        !room && {
          roomType: API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM_TYPE(roomType),
        }),

      ...(room && {
        room: API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM(room),
      }),
    };

    dispatch(createAccommodationReservation(data));
  };

  const buttons: IFormButtons = {
    primary: {
      children: blockCopy.create.peopleBlock.fields.title,
      "data-testid": "add_accommodation_reservation_submit",
    },
    secondary: {
      onClick: handleClose,
    },
  };

  const error = useMemo(() => {
    const { errorCodes } = apiMeta?.error || {};

    if (errorCodes?.includes(IJSONAPIErrorCodes.UserAlreadyHasAReservation)) {
      return blockCopy.create.peopleBlock.errors.userAlreadyPresent;
    }

    if (errorCodes?.includes(IJSONAPIErrorCodes.NoVacancyLeft)) {
      return blockCopy.create.peopleBlock.errors.noVacancyLeft;
    }

    return !!apiMeta?.error;
  }, [apiMeta?.error]);

  return (
    <Modal
      title={blockCopy.create.peopleBlock.fields.title}
      icon={User}
      showCloseButton={true}
      handleClose={handleClose}
    >
      <AccommodationReservationForm
        buttons={buttons}
        handleSubmit={handleSubmit}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        apiError={apiMeta?.error}
        error={error}
        blockId={block.id}
        isEdit={false}
      />
    </Modal>
  );
};
